import { Record } from 'immutable';

import { ExpositionHistoryListEntity } from '../components/exposition-history-list/exposition-history-list.entity';
import { ExpositionListEntity } from '../components/exposition-list/exposition-list.entity';
import { ExpositionDatablockColumnEntity } from '../components/exposition-stepper/exposition-datablock-list/exposition-datablock-column.entity';
import { ExpositionDatablockListEntity } from '../components/exposition-stepper/exposition-datablock-list/exposition-datablock-list.entity';
import { ExpositionEntity } from '../components/exposition-view/exposition.entity';
import { ExpositionColumnConfigListEntity } from '../domain/exposition-column-config-list.entity';
import { ExpositionConsumerMappingEntity } from '../domain/exposition-consumer-mapping.entity';
import { ExpositionPermissionsEntity } from '../domain/exposition-permissions.entity';

export const ExpositionSliceKey = 'all';

interface IExpositionOriginalSliceState {
	loading: boolean;
	isLoadingDatablockColumns: boolean;
	isSavingExposition: boolean;
	isLoadingJobErrorLogs: boolean;
	isLoadingMetadataInfo: boolean;
	actionInProgress: boolean;
	permissions: ExpositionPermissionsEntity;
	expositions: ExpositionListEntity;
	consumersToMap: Array<ExpositionConsumerMappingEntity>;
	inViewExposition: ExpositionEntity;
	history: ExpositionHistoryListEntity;
	stepper: {
		columns: ExpositionColumnConfigListEntity;
		datablocks: ExpositionDatablockListEntity;
	};
}

const stateDefaults: IExpositionOriginalSliceState = {
	loading: false,
	isLoadingDatablockColumns: false,
	isSavingExposition: false,
	isLoadingJobErrorLogs: false,
	isLoadingMetadataInfo: false,
	actionInProgress: false,
	consumersToMap: [],
	permissions: ExpositionPermissionsEntity.build({}),
	expositions: ExpositionListEntity.build([]),
	inViewExposition: ExpositionEntity.build(),
	history: ExpositionHistoryListEntity.build([]),
	stepper: {
		columns: ExpositionColumnConfigListEntity.build([]),
		datablocks: ExpositionDatablockListEntity.build([]),
	},
};

export class ExpositionOriginalSliceState extends Record<IExpositionOriginalSliceState>(
	stateDefaults
) {
	public static readonly key = ExpositionSliceKey;

	public toggleLoading(isLoading: boolean): ExpositionOriginalSliceState {
		return this.set('loading', isLoading);
	}

	public setActionInProgress(
		isActionInProgress: boolean
	): ExpositionOriginalSliceState {
		return this.set('actionInProgress', isActionInProgress);
	}

	public toggleIsSavingExposition(
		isLoading: boolean
	): ExpositionOriginalSliceState {
		return this.set('isSavingExposition', isLoading);
	}

	public toggleLoadingDatablockColumns(
		isLoading: boolean
	): ExpositionOriginalSliceState {
		return this.set('isLoadingDatablockColumns', isLoading);
	}

	public updateDatablockListWithColumns(
		datablockId: number,
		columns: Array<ExpositionDatablockColumnEntity>
	): ExpositionOriginalSliceState {
		const datablocks = this.stepper.datablocks.setDatablockColumns(
			datablockId,
			columns
		);
		const stepper = {
			...this.stepper,
			datablocks,
		};
		return this.set('stepper', stepper);
	}

	public setColumnConfigList(
		columns: ExpositionColumnConfigListEntity
	): ExpositionOriginalSliceState {
		return this.set('stepper', {
			...this.stepper,
			columns,
		});
	}

	public setDatablockList(
		datablocks: ExpositionDatablockListEntity
	): ExpositionOriginalSliceState {
		return this.set('stepper', {
			...this.stepper,
			datablocks,
		});
	}

	public resetStepper(): ExpositionOriginalSliceState {
		return this.merge(stateDefaults);
	}

	public setExpositionList(
		list: ExpositionListEntity
	): ExpositionOriginalSliceState {
		return this.set('expositions', list);
	}

	public setInViewExposition(
		expo: ExpositionEntity
	): ExpositionOriginalSliceState {
		return this.set('inViewExposition', expo);
	}

	public setCanCreateExposition(
		hasPermission: boolean
	): ExpositionOriginalSliceState {
		return this.set(
			'permissions',
			this.get('permissions').setHasCreateGlobalPermission(hasPermission)
		);
	}

	public setExpositionViewPermissions(permissions: {
		delete: boolean;
		view: boolean;
		edit: boolean;
	}): ExpositionOriginalSliceState {
		return this.set(
			'permissions',
			this.get('permissions').setExpositionViewPermissions(permissions)
		);
	}

	public setUnmappedConsumers(
		consumers: Array<ExpositionConsumerMappingEntity>
	): ExpositionOriginalSliceState {
		return this.set('consumersToMap', consumers);
	}

	public setHistory(
		list: ExpositionHistoryListEntity
	): ExpositionOriginalSliceState {
		return this.set('history', list);
	}
	public setIsLoadingErrorLogs(
		isLoading: boolean
	): ExpositionOriginalSliceState {
		return this.set('isLoadingJobErrorLogs', isLoading);
	}

	public toggleLoadingMetadataInfo(
		isLoading: boolean
	): ExpositionOriginalSliceState {
		return this.set('isLoadingMetadataInfo', isLoading);
	}
}
